import React, { useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Drawer } from './Drawer';
import { useStore } from '../contexts/StoreContext';
import { treatments } from '../data/treatments';
import { founder, salon } from '../data/content';
const times = ['09:30', '11:00', '13:30', '15:00', '16:30', '18:00'];
export function BookingDrawer() {
  const {
    bookingOpen,
    closeBooking,
    bookingTreatment
  } = useStore();
  const [treatment, setTreatment] = useState(treatments[0].name);
  const [dayIndex, setDayIndex] = useState<number | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const days = useMemo(() => Array.from({
    length: 8
  }, (_, i) => addDays(new Date(), i + 1)).filter(d => ![0, 1].includes(d.getDay())), []);
  useEffect(() => {
    if (bookingOpen) {
      setTreatment(bookingTreatment ?? treatments[0].name);
      setDone(false);
    }
  }, [bookingOpen, bookingTreatment]);
  const selected = treatments.find(t => t.name === treatment);
  const canSubmit = dayIndex !== null && time !== null && name.trim() !== '' && email.includes('@');
  return <Drawer open={bookingOpen} onClose={closeBooking} title={done ? 'Aanvraag verstuurd' : 'Afspraak aanvragen'} footer={done ? undefined : <button type="submit" form="booking-form" disabled={!canSubmit} className="w-full rounded-full bg-ink py-3.5 text-sm text-shell transition-colors duration-150 ease-soft hover:bg-clay disabled:cursor-not-allowed disabled:bg-line disabled:text-muted" data-id="element-24" data-mp="ry732a6n6qcl">
            {canSubmit ? 'Aanvraag versturen' : 'Kies datum, tijd en gegevens'}
          </button>} data-id="element-23" data-mp="1jmsel3b2s4m2">
      {done ? <div className="flex h-full flex-col items-center justify-center text-center" data-id="element-25" data-mp="1bk3qkohbeoik">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blush" data-id="element-26" data-mp="16m2b0dj3l7bu">
            <CheckIcon className="h-5 w-5 text-ink" strokeWidth={1.5} data-id="element-27" data-mp="1laf5t76g8k4r" />
          </span>
          <h3 className="mt-6 font-display text-2xl text-ink" data-id="element-28" data-mp="1gblqd97lqj1y">Tot binnenkort, {name}</h3>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted" data-id="element-29" data-mp="1gdy8pnh9uvfj">
            Je aanvraag voor {treatment} op{' '}
            {dayIndex !== null && format(days[dayIndex], 'EEEE d MMMM', {
          locale: nl
        })} om {time}{' '}
            is binnen. {founder.firstName} bevestigt persoonlijk binnen 24 uur.
          </p>
          <a href={salon.whatsapp} className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink px-6 py-3 text-sm text-ink transition-colors duration-150 ease-soft hover:bg-ink hover:text-shell" data-id="element-30" data-mp="xlcef7m0jcx2">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-31" data-mp="28zpxlz5tn46r" />
            Iets doorgeven via WhatsApp
          </a>
        </div> : <form id="booking-form" onSubmit={e => {
      e.preventDefault();
      setDone(true);
    }} className="space-y-8" data-id="element-32" data-mp="2lilpag90j9bh">
          <fieldset data-id="element-33" data-mp="2bkpe5og2sedd">
            <legend className="text-[11px] uppercase tracking-widest text-muted" data-id="element-34" data-mp="2bk5vg5ilplya">
              1 — Behandeling
            </legend>
            <select value={treatment} onChange={e => setTreatment(e.target.value)} className="mt-3 w-full rounded-sm border border-line bg-sand px-4 py-3 text-sm text-ink focus:border-ink focus:outline-none" data-id="element-35" data-mp="2zbl3mex26xhg">
              {treatments.map(t => <option key={t.slug} value={t.name} data-id="element-36" data-mp="2uhyuuxu0ndqx">
                  {t.name} — {t.duration}, {t.price}
                </option>)}
            </select>
            {selected && <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-37" data-mp="3n1famfup5rts">
                {selected.tagline}
                {selected.kuur ? ` · kuur: ${selected.kuur}` : ''}
              </p>}
          </fieldset>

          <fieldset data-id="element-38" data-mp="1uesqe4mxbox7">
            <legend className="text-[11px] uppercase tracking-widest text-muted" data-id="element-39" data-mp="1pd3cmh8bgx5f">2 — Dag</legend>
            <div className="mt-3 grid grid-cols-3 gap-2" data-id="element-40" data-mp="1keiecncmknjm">
              {days.map((d, i) => {
            const active = dayIndex === i;
            return <button key={d.toISOString()} type="button" aria-pressed={active} onClick={() => setDayIndex(i)} className={`rounded-sm border px-2 py-3 text-center transition-colors duration-150 ease-soft ${active ? 'border-ink bg-ink text-shell' : 'border-line text-muted hover:border-ink hover:text-ink'}`} data-id="element-41" data-mp="2i43i6wmaq8z7" data-mpi={i}>
                    <span className="block text-[11px] uppercase tracking-wide" data-id="element-42" data-mp="34aphsrvk11kz">
                      {format(d, 'EEE', {
                  locale: nl
                })}
                    </span>
                    <span className="mt-0.5 block font-display text-lg" data-id="element-43" data-mp="38cb04squy82k">{format(d, 'd')}</span>
                    <span className="block text-[11px]" data-id="element-44" data-mp="3d9t404q0px3p">{format(d, 'MMM', {
                  locale: nl
                })}</span>
                  </button>;
          })}
            </div>
          </fieldset>

          <fieldset data-id="element-45" data-mp="3n8saphr83io2">
            <legend className="text-[11px] uppercase tracking-widest text-muted" data-id="element-46" data-mp="3n7ha1qt2qr16">
              3 — Tijdstip
            </legend>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-47" data-mp="3w13rebwjbrqu">
              {times.map(t => <button key={t} type="button" aria-pressed={time === t} onClick={() => setTime(t)} className={`rounded-full border px-4 py-2 text-[13px] transition-colors duration-150 ease-soft ${time === t ? 'border-ink bg-ink text-shell' : 'border-line text-muted hover:border-ink hover:text-ink'}`} data-id="element-48" data-mp="4v192oabwqim">
                  {t}
                </button>)}
            </div>
          </fieldset>

          <fieldset className="space-y-3" data-id="element-49" data-mp="3qa3nzj0m9l71">
            <legend className="text-[11px] uppercase tracking-widest text-muted" data-id="element-50" data-mp="3q920ug6bt88y">
              4 — Jouw gegevens
            </legend>
            <div className="mt-3" data-id="element-51" data-mp="7xnm8ea1rgnf">
              <label htmlFor="booking-name" className="sr-only" data-id="element-52" data-mp="2xj46jthqdv3">
                Naam
              </label>
              <input id="booking-name" value={name} onChange={e => setName(e.target.value)} placeholder="Naam" className="w-full rounded-sm border border-line bg-sand px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-ink focus:outline-none" data-id="element-53" data-mp="hua3kt35zpkp" />
            </div>
            <div data-id="element-54" data-mp="u4hz42jcbgkx">
              <label htmlFor="booking-email" className="sr-only" data-id="element-55" data-mp="u3zscbz1xjyr">
                E-mailadres
              </label>
              <input id="booking-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-mailadres" className="w-full rounded-sm border border-line bg-sand px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-ink focus:outline-none" data-id="element-56" data-mp="z2b5owok2clq" />
            </div>
            <p className="text-[12px] leading-relaxed text-muted" data-id="element-57" data-mp="fgww08mgthd0">
              Je aanvraag is vrijblijvend. {founder.firstName} bevestigt persoonlijk binnen 24 uur
              en stemt de behandeling vooraf met je af. Liever mailen? {salon.email}
            </p>
          </fieldset>
        </form>}
    </Drawer>;
}